import { Injectable } from '@angular/core';
import { IMediaContentApi } from '@interfaces/campaign-analytics-api.interface';
import { TMediaType } from '@shared/components/carousel/carousel.interface';

@Injectable({
  providedIn: 'root',
})
export class MediaUtilitiesService {
  private readonly videoExtensions = [
    '.mp4',
    '.avi',
    '.mov',
    '.wmv',
    '.flv',
    '.webm',
    '.mkv',
    '.m4v',
    '.3gp',
    '.ogv',
    '.youtube',
  ];

  private readonly imageExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.svg',
    '.webp',
    '.ico',
    '.tiff',
    '.tif',
  ];

  public getMediaType(url: string): TMediaType {
    if (!url) {
      return 'image';
    }

    const lowercaseUrl = url.toLowerCase();
    const isVideo = this.videoExtensions.some((ext) => lowercaseUrl.includes(ext));

    if (isVideo) {
      return 'video';
    }

    const isImage = this.imageExtensions.some((ext) => lowercaseUrl.includes(ext));

    if (isImage) {
      return 'image';
    }

    return 'image';
  }

  public isVideo(url: string): boolean {
    return this.getMediaType(url) === 'video';
  }

  public isImage(url: string): boolean {
    return this.getMediaType(url) === 'image';
  }

  public async getVideoDurationSeconds(url: string): Promise<number> {
    return new Promise<number>((resolve): void => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      const cleanup = (): void => {
        video.removeAttribute('src');
        video.load();
      };

      video.onloadedmetadata = (): void => {
        const seconds = Number.isFinite(video.duration) ? Math.max(0, video.duration) : 0;
        cleanup();
        resolve(seconds);
      };

      video.onerror = (): void => {
        cleanup();
        resolve(0);
      };

      video.src = url;
    });
  }

  public async getDurationsForMedia(mediaList: IMediaContentApi[]): Promise<number[]> {
    const tasks: Array<Promise<number>> = mediaList.map((m: IMediaContentApi): Promise<number> => {
      if (!m?.isVideo || !m?.url) {
        return Promise.resolve(0);
      }

      return this.getVideoDurationSeconds(m.url);
    });

    const results = await Promise.allSettled(tasks);

    return results.map((res) => (res.status === 'fulfilled' ? res.value : 0));
  }
}
